/**
 * Bubble form sheet — the focused surface a `render_ui` form gets inside the
 * embedded widget.
 *
 * Why a portal instead of the composer-slot pattern the plan card uses: that
 * mechanism lives in `chat.tsx` and is shared with main chat, which this change
 * must not change. Inside the widget iframe
 * `document.body` IS the panel, so a portalled sheet covers the transcript AND
 * the composer — "the visitor can't type past the form" comes from geometry
 * rather than from cross-component state.
 *
 * The sheet deliberately stops below the widget header: that header carries the
 * brand, the language switcher and the ✕ that closes the whole widget, and
 * covering it would trap a visitor inside a form on someone else's page.
 *
 * @module components/chat/bubble/bubble-form-overlay
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ClipboardList, X } from 'lucide-react'
import { getChromeStrings, getEmbedLocale, isRtlLocale } from '@/lib/embed-locale'

/** The widget header the sheet tucks under. */
const HEADER_SELECTOR = '[data-testid="chat-bubble-header"]'

/** Everything that can hold focus inside the sheet. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export interface BubbleFormOverlayProps {
  /** Form title — shown in the sheet header and used as the dialog's a11y name. */
  title: string
  /**
   * Whether the sheet is showing. When false the sheet is HIDDEN, not
   * unmounted: the card owns its wizard step and field values in local state,
   * so unmounting would silently discard whatever the visitor had typed before
   * they closed it.
   */
  open: boolean
  /** Close the sheet. Signals nothing to the agent. */
  onClose: () => void
  /** The form card, rendered with `layout="panel"`. */
  children: ReactNode
}

/**
 * Renders its children as a sheet over the widget panel.
 *
 * Knows nothing about chats, agents, forms or specs — it is chrome, so any
 * future interactive surface can reuse it as-is.
 */
export function BubbleFormOverlay({ title, open, onClose, children }: BubbleFormOverlayProps) {
  const locale = getEmbedLocale() ?? 'en'
  const strings = getChromeStrings(locale)
  const isRtl = isRtlLocale(locale)

  /**
   * Distance from the top of the panel to the bottom of the widget header.
   * Measured rather than hardcoded so a header that grows (a longer title
   * wrapping, a future badge) can't end up overlapped by the sheet.
   */
  const [topOffset, setTopOffset] = useState(0)

  useEffect(() => {
    const header = document.querySelector(HEADER_SELECTOR)
    const measure = () => {
      setTopOffset(header ? Math.round(header.getBoundingClientRect().height) : 0)
    }
    measure()

    // Re-measured on every open, not just on mount: a header that is still
    // laying out when this first runs would otherwise pin the offset at 0 for
    // the session, and the sheet would cover the brand, the language switch and
    // the widget's own close button.
    window.addEventListener('resize', measure)
    const observer = header ? new ResizeObserver(measure) : null
    if (header) observer?.observe(header)
    return () => {
      window.removeEventListener('resize', measure)
      observer?.disconnect()
    }
  }, [open])

  const sheetRef = useRef<HTMLDivElement | null>(null)

  /** Element that had focus before the sheet took it, so we can hand it back. */
  const returnFocusRef = useRef<Element | null>(null)

  const bodyRef = useRef<HTMLDivElement | null>(null)

  /** Focus the first FIELD when the sheet appears; restore focus when it goes. */
  useEffect(() => {
    if (!open) return
    returnFocusRef.current = document.activeElement
    // Next frame: the card's fields mount with the sheet, so querying now finds
    // only the header. Search the BODY first — the close button is the sheet's
    // first focusable in DOM order, and landing there means a keyboard visitor
    // starts on "get me out of here" instead of on the first question.
    const id = window.requestAnimationFrame(() => {
      const target =
        bodyRef.current?.querySelector<HTMLElement>(FOCUSABLE) ??
        sheetRef.current?.querySelector<HTMLElement>(FOCUSABLE)
      target?.focus()
    })
    return () => {
      window.cancelAnimationFrame(id)
      const previous = returnFocusRef.current
      if (previous instanceof HTMLElement && document.contains(previous)) previous.focus()
    }
  }, [open])

  /**
   * Esc closes the FORM, not the widget.
   *
   * Safe by construction: the embed loader's Esc-to-close listener is bound to
   * the HOST page's document, and a keydown inside this iframe never reaches it
   * — so the key is ours to take without the panel closing underneath us.
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab') return

      // Focus trap: the sheet is modal, so Tab must cycle inside it.
      const focusable = Array.from(
        sheetRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
      ).filter((el) => el.offsetParent !== null)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    },
    [onClose],
  )

  /**
   * Lock the transcript behind the sheet so a scroll gesture that runs past the
   * form's ends doesn't drag the conversation around underneath it.
   */
  useEffect(() => {
    if (!open) return
    const { body } = document
    const previous = body.style.overflow
    body.style.overflow = 'hidden'
    return () => {
      body.style.overflow = previous
    }
  }, [open])

  return createPortal(
    <div
      data-testid="bubble-form-overlay-root"
      aria-hidden={open ? undefined : true}
      style={{ top: topOffset, display: open ? undefined : 'none' }}
      className="fixed inset-x-0 bottom-0 z-50"
    >
      {/*
        Scrim over the conversation only — never over the widget header, so the
        brand, the language switch and the widget's own ✕ stay reachable. It
        also keeps the last few messages faintly visible, which is what makes
        this read as a card ON the conversation rather than a new screen.
      */}
      <div
        className="absolute inset-0 bg-foreground/20 animate-in fade-in-0 duration-200"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={sheetRef}
        data-testid="bubble-form-overlay"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onKeyDown={handleKeyDown}
        dir={isRtl ? 'rtl' : 'ltr'}
        // Inset + rounded + elevated so it reads as a card floating over the
        // conversation, the way the plan card does in main chat — not a slab
        // that replaced the screen. Height is capped rather than filled, so a
        // sliver of transcript stays visible above it.
        //
        // `--app-vh` (the VISIBLE viewport the app publishes) rather than
        // 100vh, so a soft keyboard can't push Submit under the fold.
        style={{
          maxHeight: `calc(var(--app-vh, 100dvh) - ${topOffset}px - 2.5rem)`,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        className="absolute inset-x-2 bottom-2 flex max-h-full flex-col overflow-hidden rounded-xl border border-border bg-card shadow-lg shadow-black/10 animate-in slide-in-from-bottom-4 fade-in-0 duration-300"
      >
        <div className="flex shrink-0 items-start gap-2.5 border-b border-border px-3.5 py-3">
          <div className="grid size-8 shrink-0 place-items-center rounded-md bg-muted text-foreground">
            <ClipboardList className="size-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold leading-tight text-foreground">{title}</p>
            <p className="truncate text-[11.5px] text-muted-foreground">{strings.formSubtitle}</p>
          </div>
          <button
            type="button"
            data-testid="bubble-form-close"
            onClick={onClose}
            aria-label={strings.closeForm}
            title={strings.closeForm}
            className="-me-1 grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </div>

        <div ref={bodyRef} className="min-h-0 flex-1 overflow-hidden">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}
